/**
 * Klorah Analytics Service
 * Integrates with PostHog via our custom backend
 */

class AnalyticsService {
  constructor() {
    this.baseUrl = process.env.REACT_APP_API_URL || 'https://klorah-fast-server-my8l.onrender.com/api';
    
    this.userId = null;
    this.userProperties = {};
    this.sessionId = this.generateSessionId();
    this.isEnabled = true;
    
    // Initialize analytics
    this.init();
  }

  /**
   * Initialize analytics service
   */
  init() {
    try {
      // Check if analytics is disabled (for development/testing)
      if (localStorage.getItem('klorah_analytics_disabled') === 'true') {
        this.isEnabled = false;
        console.log('📊 Klorah Analytics: Disabled');
        return;
      }

      // Restore user session if exists
      const savedUserId = localStorage.getItem('klorah_user_id');
      if (savedUserId) {
        this.userId = savedUserId;
      }

      console.log('📊 Klorah Analytics: Initialized');
      
      // Track page load
      this.trackPageView();
      
    } catch (error) {
      console.error('Analytics initialization error:', error);
    }
  }

  /**
   * Generate unique session ID
   */
  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Make request to analytics backend
   */
  async makeRequest(endpoint, data) {
    if (!this.isEnabled) return;

    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...data,
          client_platform: 'web',
          session_id: this.sessionId,
          timestamp: new Date().toISOString()
        })
      });

      if (!response.ok) {
        throw new Error(`Analytics request failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Analytics request error:', error);
      // Fail silently to not break user experience
    }
  }

  /**
   * Identify user with properties
   */
  async identify(userId, properties = {}) {
    this.userId = userId;
    this.userProperties = { ...this.userProperties, ...properties };
    
    // Save to localStorage for session persistence
    localStorage.setItem('klorah_user_id', userId);

    await this.makeRequest('/identify', {
      user_id: userId,
      properties: {
        ...properties,
        platform: 'web',
        user_agent: navigator.userAgent,
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: navigator.language
      }
    });

    console.log('📊 User identified:', userId);
  }

  /**
   * Track custom event
   */
  async track(eventName, properties = {}) {
    await this.makeRequest('/track/event', {
      event_name: eventName,
      user_id: this.userId || 'anonymous',
      properties: {
        ...properties,
        url: window.location.href,
        referrer: document.referrer,
        user_agent: navigator.userAgent
      }
    });

    console.log('📊 Event tracked:', eventName, properties);
  }

  /**
   * Track user signup
   */
  async trackSignup(signupData) {
    await this.makeRequest('/track/signup', {
      user_id: this.userId,
      signup_data: {
        ...signupData,
        signup_timestamp: new Date().toISOString(),
        referrer: document.referrer,
        utm_source: this.getUrlParameter('utm_source'),
        utm_medium: this.getUrlParameter('utm_medium'),
        utm_campaign: this.getUrlParameter('utm_campaign')
      }
    });

    console.log('📊 Signup tracked:', signupData);
  }

  /**
   * Track onboarding step
   */
  async trackOnboarding(stage, stepData = {}) {
    await this.makeRequest('/track/onboarding', {
      user_id: this.userId,
      step_data: {
        stage,
        ...stepData,
        timestamp: new Date().toISOString()
      }
    });

    console.log('📊 Onboarding tracked:', stage, stepData);
  }

  /**
   * Track subscription event
   */
  async trackSubscription(subscriptionData) {
    await this.makeRequest('/track/subscription', {
      user_id: this.userId,
      subscription_data: {
        ...subscriptionData,
        subscription_timestamp: new Date().toISOString()
      }
    });

    console.log('📊 Subscription tracked:', subscriptionData);
  }

  /**
   * Track page view
   */
  async trackPageView(pageData = {}) {
    const defaultPageData = {
      page: window.location.pathname,
      title: document.title,
      url: window.location.href,
      referrer: document.referrer,
      search: window.location.search,
      hash: window.location.hash
    };

    await this.makeRequest('/track/pageview', {
      user_id: this.userId || 'anonymous',
      page_data: {
        ...defaultPageData,
        ...pageData
      }
    });
  }

  /**
   * Photography Business Specific Events
   */

  // Invoice events
  async trackInvoiceCreated(invoiceData) {
    await this.track('Invoice Created', {
      invoice_id: invoiceData.id,
      amount: invoiceData.amount,
      currency: invoiceData.currency || 'USD',
      client_id: invoiceData.clientId,
      due_date: invoiceData.dueDate,
      category: 'billing'
    });
  }

  async trackInvoiceSent(invoiceData) {
    await this.track('Invoice Sent', {
      invoice_id: invoiceData.id,
      amount: invoiceData.amount,
      client_id: invoiceData.clientId,
      delivery_method: invoiceData.deliveryMethod || 'email',
      category: 'billing'
    });
  }

  async trackPaymentReceived(paymentData) {
    await this.track('Payment Received', {
      invoice_id: paymentData.invoiceId,
      amount: paymentData.amount,
      currency: paymentData.currency || 'USD',
      payment_method: paymentData.method,
      category: 'billing'
    });
  }

  // Contract events
  async trackContractCreated(contractData) {
    await this.track('Contract Created', {
      contract_id: contractData.id,
      client_id: contractData.clientId,
      contract_type: contractData.type,
      value: contractData.value,
      category: 'legal'
    });
  }

  async trackContractSigned(contractData) {
    await this.track('Contract Signed', {
      contract_id: contractData.id,
      client_id: contractData.clientId,
      signature_method: contractData.signatureMethod,
      category: 'legal'
    });
  }

  // Client management events
  async trackClientAdded(clientData) {
    await this.track('Client Added', {
      client_id: clientData.id,
      client_type: clientData.type,
      source: clientData.source,
      category: 'crm'
    });
  }

  // Photo/Gallery events
  async trackPhotoUploaded(photoData) {
    await this.track('Photo Uploaded', {
      photo_count: photoData.count || 1,
      gallery_id: photoData.galleryId,
      client_id: photoData.clientId,
      file_size_mb: photoData.fileSizeMB,
      category: 'content'
    });
  }

  async trackGalleryShared(galleryData) {
    await this.track('Gallery Shared', {
      gallery_id: galleryData.id,
      client_id: galleryData.clientId,
      photo_count: galleryData.photoCount,
      share_method: galleryData.shareMethod,
      category: 'sharing'
    });
  }

  // Email events
  async trackEmailSent(emailData) {
    await this.track('Email Sent', {
      email_type: emailData.type,
      recipient_type: emailData.recipientType,
      template_used: emailData.templateUsed,
      client_id: emailData.clientId,
      category: 'communication'
    });
  }

  // Feature usage events
  async trackFeatureUsed(featureName, featureData = {}) {
    await this.track(`Feature Used: ${featureName}`, {
      feature: featureName.toLowerCase().replace(/\s+/g, '_'),
      ...featureData,
      category: 'feature_usage'
    });
  }

  // Business metrics
  async trackProjectCreated(projectData) {
    await this.track('Project Created', {
      project_id: projectData.id,
      project_type: projectData.type,
      estimated_value: projectData.estimatedValue,
      client_id: projectData.clientId,
      category: 'business'
    });
  }

  async trackReportGenerated(reportData) {
    await this.track('Report Generated', {
      report_type: reportData.type,
      date_range: reportData.dateRange,
      category: 'analytics'
    });
  }

  /**
   * Utility methods
   */
  getUrlParameter(name) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(name);
  }

  /**
   * Disable analytics (for testing/development)
   */
  disable() {
    this.isEnabled = false;
    localStorage.setItem('klorah_analytics_disabled', 'true');
    console.log('📊 Klorah Analytics: Disabled');
  }

  /**
   * Enable analytics
   */
  enable() {
    this.isEnabled = true;
    localStorage.removeItem('klorah_analytics_disabled');
    console.log('📊 Klorah Analytics: Enabled');
  }

  /**
   * Clear user session
   */
  reset() {
    this.userId = null;
    this.userProperties = {};
    this.sessionId = this.generateSessionId();
    localStorage.removeItem('klorah_user_id');
    console.log('📊 Analytics session reset');
  }
}

// Create singleton instance
const analytics = new AnalyticsService();

export default analytics; 